/**
 * Optional mood -> search-query interpretation via the Claude API.
 * No React imports.
 *
 * Browser access requires the header
 * `anthropic-dangerous-direct-browser-access: true`. This is load-bearing and
 * was verified by probe: WITHOUT it the response carries no
 * Access-Control-Allow-Origin and the browser blocks it; WITH it the response
 * carries `ACAO: *` and lists the header in `vary`. See DECISIONS.md.
 *
 * Model is `claude-haiku-4-5` with max_tokens 64 — this is a one-line string
 * transform, and paying Sonnet prices for it on every search would be silly.
 *
 * FAILURE POLICY: this module never throws and never reports an error
 * upstream. Unset key, 401, 429, network failure, malformed body, refusal,
 * empty completion — all of them return the same thing the unconfigured path
 * returns, which is `buildFallbackQuery(input)`. One code path, one fallback.
 * The widget is never disabled and never shows the user an Anthropic error.
 */
import { fetchJson } from '../http';
import { buildFallbackQuery } from './query';

const ENDPOINT = 'https://api.anthropic.com/v1/messages';
const MODEL = 'claude-haiku-4-5';
const MAX_TOKENS = 64;

const SYSTEM_PROMPT = [
  'You convert a short note about someone\'s mood, or an artist or genre they',
  'named, into a music search query for the Deezer catalogue.',
  'Reply with the query and nothing else: no quotes, no punctuation at the',
  'ends, no explanation, no preamble.',
  'Two to five words. Prefer concrete musical language — a genre, an era, an',
  'instrument, an artist name — over restating the feeling.',
  'If they named an artist, album or song, return that name essentially as-is.',
].join(' ');

/** A query longer than this is the model ignoring its instructions. */
const MAX_ACCEPTED_CHARS = 80;

interface MessagesResponse {
  readonly content?: ReadonlyArray<{ type?: string; text?: string }>;
}

/**
 * Strip the ways a model says more than it was asked to. Anything still
 * suspect (multi-line, over-long, empty) is rejected by the caller in favour
 * of the deterministic fallback.
 */
function cleanCompletion(text: string): string | null {
  const firstLine = text.split('\n').map((l) => l.trim()).find((l) => l !== '');
  if (firstLine === undefined) return null;
  const stripped = firstLine
    .replace(/^["'`\s]+|["'`\s.]+$/g, '')
    .replace(/^(?:search|query|try)\s*:\s*/i, '')
    .trim();
  if (stripped === '' || stripped.length > MAX_ACCEPTED_CHARS) return null;
  return stripped;
}

export interface MoodResult {
  readonly query: string;
  /** true only when Claude was called AND returned something usable. */
  readonly interpreted: boolean;
}

/**
 * Resolve free text to a Deezer query.
 *
 * @param apiKey `undefined` when VITE_ANTHROPIC_API_KEY is unset — the normal
 *               zero-config case, which skips the network entirely.
 */
export async function resolveQuery(
  input: string,
  apiKey: string | undefined,
  signal?: AbortSignal,
): Promise<MoodResult> {
  const fallback: MoodResult = { query: buildFallbackQuery(input), interpreted: false };
  if (apiKey === undefined || input.trim() === '') return fallback;

  const res = await fetchJson<MessagesResponse>(ENDPOINT, {
    method: 'POST',
    timeoutMs: 8_000,
    ...(signal !== undefined ? { signal } : {}),
    headers: {
      'content-type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      // The switch that makes the response CORS-readable. Without it the
      // request succeeds at the network layer and is discarded by the browser.
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: MAX_TOKENS,
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: input.trim().slice(0, 500) }],
    }),
  });

  if (!res.ok) return fallback;

  const block = res.data.content?.find((c) => c.type === 'text');
  const text = typeof block?.text === 'string' ? cleanCompletion(block.text) : null;
  return text === null ? fallback : { query: text, interpreted: true };
}
